import React, { useState, useRef, useEffect, useMemo } from "react"
import { TextField } from "@terveystalo/design-system-react-components"

export interface AutocompleteOption {
  label: string
  value: string
}

interface AutocompleteProps {
  id: string
  name: string
  label: string
  placeholder?: string
  options: AutocompleteOption[]
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
  maxHeight?: string
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  id,
  name,
  label,
  placeholder,
  options,
  value,
  onChange,
  required = false,
  disabled = false,
  maxHeight = "300px",
}) => {
  const [inputValue, setInputValue] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isTypingRef = useRef(false)
  const autoSelectedLabelRef = useRef("")

  // Get the display label for the selected value (excluding empty placeholder)
  const selectedOption = options.find((opt) => opt.value === value && opt.value !== "")

  // Update input value when value prop changes (but not while typing)
  useEffect(() => {
    if (isTypingRef.current) return
    if (selectedOption && value) {
      setInputValue(selectedOption.label)
    } else {
      setInputValue("")
    }
  }, [value, selectedOption])

  // Filter options based on input
  const filteredOptions = useMemo(() => {
    // Filter out the empty placeholder option
    const validOptions = options.filter((opt) => opt.value !== "")

    if (!inputValue) {
      // Show all valid options (up to 100) when input is empty
      return validOptions.slice(0, 100)
    }

    const searchTerm = inputValue.toLowerCase()
    return validOptions.filter((opt) => opt.label.toLowerCase().includes(searchTerm)).slice(0, 100) // Limit to 100 results for performance
  }, [inputValue, options])

  // Stop typing mode and update input to show the selected option's label
  const stopTyping = () => {
    if (!isTypingRef.current) return
    isTypingRef.current = false
    if (autoSelectedLabelRef.current) {
      setInputValue(autoSelectedLabelRef.current)
    }
    autoSelectedLabelRef.current = ""
  }

  // Auto-select first filtered option while typing
  useEffect(() => {
    if (!isTypingRef.current) return
    if (inputValue && filteredOptions.length > 0) {
      onChange(filteredOptions[0].value)
      autoSelectedLabelRef.current = filteredOptions[0].label
      setHighlightedIndex(0)
    } else if (inputValue && filteredOptions.length === 0) {
      onChange("")
      autoSelectedLabelRef.current = ""
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredOptions])

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    isTypingRef.current = !!newValue
    setInputValue(newValue)
    setIsOpen(true)
    setHighlightedIndex(newValue ? 0 : -1)

    // If input is cleared, clear the selection
    if (!newValue) {
      onChange("")
      autoSelectedLabelRef.current = ""
    }
  }

  // Handle option selection
  const handleOptionClick = (option: AutocompleteOption) => {
    isTypingRef.current = false
    setInputValue(option.label)
    onChange(option.value)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Tab: Open dropdown and highlight first option, but don't prevent moving to next field
    if (e.key === "Tab") {
      if (!isOpen) {
        e.preventDefault()
        setIsOpen(true)
        setHighlightedIndex(0)
        return
      }
      // If dropdown is open but nothing highlighted, highlight first option
      if (highlightedIndex < 0) {
        e.preventDefault()
        setHighlightedIndex(0)
        return
      }
      // Otherwise, allow default Tab behavior to move to next field
      return
    }

    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setIsOpen(true)
      return
    }

    if (!isOpen) return

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev))
        break
      case "ArrowUp":
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case "Enter":
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionClick(filteredOptions[highlightedIndex])
        }
        break
      case "Escape":
        stopTyping()
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
      default:
        break
    }
  }

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: "nearest", behavior: "smooth" })
      }
    }
  }, [highlightedIndex])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        stopTyping()
        setIsOpen(false)
        setIsFocused(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Render label with the matched portion bolded
  const renderHighlightedLabel = (label: string) => {
    if (!inputValue) return label
    const lowerLabel = label.toLowerCase()
    const lowerSearch = inputValue.toLowerCase()
    const matchIndex = lowerLabel.indexOf(lowerSearch)
    if (matchIndex === -1) return label
    const before = label.slice(0, matchIndex)
    const match = label.slice(matchIndex, matchIndex + inputValue.length)
    const after = label.slice(matchIndex + inputValue.length)
    return (
      <>
        {before}
        <strong>{match}</strong>
        {after}
      </>
    )
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <TextField
        id={id}
        name={name}
        label={label}
        placeholder={isFocused ? "" : placeholder || label}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => {
          setIsFocused(true)
          setIsOpen(true)
        }}
        onBlur={() => {
          // Delay to allow click events to fire first
          setTimeout(() => {
            stopTyping()
            setIsFocused(false)
            setIsOpen(false)
            setHighlightedIndex(-1)
          }, 150)
        }}
        onKeyDown={handleKeyDown}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />

      {isOpen && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 bg-white shadow-lg"
          style={{
            maxHeight,
            overflowY: "auto",
            top: isFocused ? "calc(100% - 4px)" : "calc(100% - 2px)",
            left: isFocused ? "-2px" : "-1px",
            right: isFocused ? "-2px" : "-1px",
            border: isFocused ? "2px solid #0066CC" : "1px solid #000",
            borderTop: "none",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
          role="listbox"
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option.value}
              className={`px-4 py-2 cursor-pointer transition-colors duration-150 ${
                highlightedIndex === index ? "bg-gray-200" : "hover:bg-gray-150"
              } ${value === option.value ? "bg-blue-100 font-semibold" : ""} active:bg-gray-300`}
              style={{
                backgroundColor:
                  value === option.value
                    ? highlightedIndex === index
                      ? "#DBEAFE"
                      : "#EFF6FF"
                    : highlightedIndex === index
                      ? "#E5E7EB"
                      : "transparent",
                color: value && value !== option.value ? "#6B7280" : undefined,
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.backgroundColor = "#D1D5DB"
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.backgroundColor =
                  highlightedIndex === index ? "#E5E7EB" : "transparent"
              }}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  handleOptionClick(option)
                }
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              role="option"
              aria-selected={value === option.value}
              tabIndex={-1}
            >
              {renderHighlightedLabel(option.label)}
            </div>
          ))}
        </div>
      )}

      {isOpen && filteredOptions.length === 0 && inputValue && (
        <div
          className="absolute z-50 bg-white shadow-lg"
          style={{
            top: isFocused ? "calc(100% - 4px)" : "calc(100% - 2px)",
            left: isFocused ? "-2px" : "-1px",
            right: isFocused ? "-2px" : "-1px",
            border: isFocused ? "2px solid #0066CC" : "1px solid #000",
            borderTop: "none",
            borderBottomLeftRadius: "8px",
            borderBottomRightRadius: "8px",
          }}
        >
          <div className="px-4 py-2 text-gray-500">Ei tuloksia</div>
        </div>
      )}
    </div>
  )
}
